import { Autocomplete, Box, Button, Checkbox, Chip, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import { getLabelByAccess, getMaxAccessByPermissions } from "@entrilia/shared/utilities/accessHelper.ts";
import type {
  GroupBasedAuthorization,
  GroupForSharing,
  UserBasedAuthorization,
  UserForSharing,
} from "../../../../../../../../api/types/svTypes.ts";
import { AccessModeMenu } from "./AccessModeMenu.tsx";
import AuthorizationUserItem from "./AuthorizationUserItem.tsx";
import AuthorizationGroupItem from "./userGroups/AuthorizationGroupItem.tsx";

type SharingOption = { type: "group"; group: GroupForSharing } | { type: "user"; user: UserForSharing };

const getOptionKey = (option: SharingOption): string =>
  option.type === "group" ? `group-${option.group.groupId}` : `user-${option.user.userId}`;

const getOptionLabel = (option: SharingOption): string =>
  option.type === "group" ? option.group.name : `${option.user.name}${option.user.email}`;

interface UserSelectionProps {
  availableUsers: UserForSharing[];
  availableGroups: GroupForSharing[];
  authorizedUsers: UserBasedAuthorization[];
  authorizedGroups: GroupBasedAuthorization[];
  disabled: boolean;
  currentUserId: string;
  onUsersSelected: (users: UserForSharing[], access: ReportAccessType) => void;
  onGroupsSelected: (groups: GroupForSharing[], access: ReportAccessType) => void;
}

export default function UserSelection({
  availableUsers,
  availableGroups,
  authorizedUsers,
  authorizedGroups,
  disabled,
  currentUserId,
  onUsersSelected,
  onGroupsSelected,
}: UserSelectionProps) {
  const [selectedOptions, setSelectedOptions] = useState<SharingOption[]>([]);
  const [accessModeMenuOpen, setAccessModeMenuOpen] = useState(false);
  const [menuRef, setMenuRef] = useState<HTMLDivElement>();
  const [selectedAccess, setSelectedAccess] = useState<ReportAccessType>(ReportAccessType.Read);

  const filterOptionsByAccess = useCallback(
    (opts: SharingOption[], access: ReportAccessType): SharingOption[] =>
      opts.filter((option) => {
        if (option.type === "group") return true;
        switch (access) {
          case ReportAccessType.FullAccess:
            return option.user.permissions.includes("ShareReports");
          case ReportAccessType.Write:
            return option.user.permissions.includes("ManageReports");
          case ReportAccessType.Read:
            return option.user.permissions.includes("ViewReports");
          default:
            return false;
        }
      }),
    []
  );

  const handleAccessChanged = useCallback(
    (access: ReportAccessType) => {
      setSelectedAccess(access);
      setSelectedOptions((prev) => filterOptionsByAccess(prev, access));
    },
    [filterOptionsByAccess]
  );

  const selectedAccessLabel = useMemo(() => getLabelByAccess(selectedAccess), [selectedAccess]);

  const usersWithoutAuthorization = useMemo(() => {
    return availableUsers.filter((u) => !authorizedUsers.some((au) => au.userId === u.userId));
  }, [availableUsers, authorizedUsers]);

  const groupsWithoutAuthorization = useMemo(() => {
    return availableGroups.filter((g) => !authorizedGroups.some((ag) => ag.groupId === g.groupId));
  }, [availableGroups, authorizedGroups]);

  const options = useMemo(() => {
    const groupOptions: SharingOption[] = groupsWithoutAuthorization.map((g) => ({ type: "group", group: g }));

    const userOptions: SharingOption[] = usersWithoutAuthorization
      .filter((user) => {
        switch (selectedAccess) {
          case ReportAccessType.FullAccess:
            return user.permissions.includes("ShareReports");
          case ReportAccessType.Write:
            return user.permissions.includes("ManageReports");
          case ReportAccessType.Read:
            return user.permissions.includes("ViewReports");
          default:
            return false;
        }
      })
      .map((u) => ({ type: "user", user: u }));

    return [...groupOptions, ...userOptions];
  }, [selectedAccess, usersWithoutAuthorization, groupsWithoutAuthorization]);

  const handleAdd = useCallback(() => {
    const users = selectedOptions.filter((o): o is { type: "user"; user: UserForSharing } => o.type === "user");
    const groups = selectedOptions.filter((o): o is { type: "group"; group: GroupForSharing } => o.type === "group");

    if (users.length > 0) {
      onUsersSelected(
        users.map((o) => o.user),
        selectedAccess
      );
    }
    if (groups.length > 0) {
      onGroupsSelected(
        groups.map((o) => o.group),
        selectedAccess
      );
    }
    setSelectedOptions([]);
  }, [onUsersSelected, onGroupsSelected, selectedOptions, selectedAccess]);

  return (
    <Stack direction={"row"} spacing={1} sx={{ alignItems: "start" }}>
      <Autocomplete
        multiple
        fullWidth
        value={selectedOptions}
        options={options}
        disabled={disabled}
        disableCloseOnSelect
        groupBy={(option) => (option.type === "group" ? "User Groups" : "Users")}
        getOptionLabel={getOptionLabel}
        getOptionKey={getOptionKey}
        isOptionEqualToValue={(option, value) => getOptionKey(option) === getOptionKey(value)}
        sx={(theme) => ({
          "&.MuiAutocomplete-hasPopupIcon .MuiInputBase-root": {
            pr: 10,
            '[data-id="access-button"]': {
              right: 35,
            },
          },
          "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiInputBase-root": {
            pr: 15,
            '[data-id="access-button"]': {
              right: 63,
            },
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
        })}
        renderValue={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...rest } = getTagProps({ index });
            const label = option.type === "group" ? option.group.name : option.user.name;
            return (
              <Chip
                key={key}
                label={label}
                variant="outlined"
                sx={(theme) => ({ borderColor: theme.palette.divider, fontSize: "12px", fontWeight: 500 })}
                {...rest}
              />
            );
          })
        }
        renderOption={({ key: _key, ...props }, option, { selected }) => (
          <Box component={"li"} key={getOptionKey(option)} {...props}>
            <Checkbox checked={selected} />
            {option.type === "group" ? (
              <AuthorizationGroupItem
                name={option.group.name}
                memberCount={option.group.memberCount}
                iconSize="small"
              />
            ) : (
              <Stack direction={"row"} sx={{ flex: 1 }}>
                <AuthorizationUserItem
                  name={option.user.name}
                  email={option.user.email}
                  isCurrentUser={option.user.userId === currentUserId}
                  iconSize="small"
                />
                <HorizontalFill />
                <Typography variant="body2" color={"secondary"}>
                  {getMaxAccessByPermissions(option.user.permissions)}
                </Typography>
              </Stack>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedOptions.length === 0 ? "Member name or group, separated by commas" : ""}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                sx: {
                  minHeight: 40,
                },
                endAdornment: (
                  <>
                    {params.slotProps.input.endAdornment}
                    <Box
                      ref={setMenuRef}
                      data-id="access-button"
                      sx={{
                        position: "absolute",
                        top: "50%",
                        transform: "translate(0, -50%)",
                        cursor: disabled ? "default" : "pointer",
                      }}
                    >
                      <Typography variant="body2" onClick={() => !disabled && setAccessModeMenuOpen(true)}>
                        {selectedAccessLabel}
                      </Typography>
                    </Box>
                  </>
                ),
              },
            }}
          />
        )}
        onChange={(_, newValue) => {
          setSelectedOptions(newValue);
        }}
      />
      <Box>
        <Button
          variant="contained"
          sx={{ height: 39 }}
          disabled={selectedOptions.length === 0 || disabled}
          size="medium"
          onClick={handleAdd}
        >
          Apply
        </Button>
      </Box>
      <AccessModeMenu
        menuRef={menuRef}
        open={accessModeMenuOpen}
        access={selectedAccess}
        allowedAccesses={[ReportAccessType.FullAccess, ReportAccessType.Write, ReportAccessType.Read]}
        onSelect={handleAccessChanged}
        onClose={() => setAccessModeMenuOpen(false)}
      />
    </Stack>
  );
}
